import type { GeoPlaceDto } from '@kominiarz/shared';
import { MapPin } from 'lucide-react';
import { useId, useState, type KeyboardEvent } from 'react';
import { MIN_ADDRESS_CHARS, useAddressSuggestions } from '../lib/geoApi';
import styles from './Combobox.module.css';
import { describedBy, Field } from './form/Field';
import { PinControl, type Coords } from './PinControl';

export type { Coords };

interface AddressFieldProps {
  label: string;
  value: string;
  coords: Coords | null;
  /** Typing: the text changed, so any stored pin no longer applies. */
  onType: (address: string) => void;
  /** A suggestion with an exact house was chosen: address and pin together. */
  onPick: (address: string, coords: Coords) => void;
  /** The pin was placed or moved on the map. */
  onPin: (coords: Coords) => void;
  error?: string;
  hint?: string;
  optional?: boolean;
  placeholder?: string;
}

/**
 * Address input with suggestions while typing (Photon via our API). The text can
 * always be saved as typed; the pin is optional and can be placed on a map.
 */
export function AddressField({ label, value, coords, onType, onPick, onPin, error, hint, optional, placeholder }: AddressFieldProps) {
  const id = useId();
  const listId = `${id}-list`;
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const suggestions = useAddressSuggestions(value, open);
  const places = open && value.trim().length >= MIN_ADDRESS_CHARS ? (suggestions.data?.places ?? []) : [];
  const unavailable = suggestions.data !== undefined && !suggestions.data.available;
  const showList = open && places.length > 0;
  const searching = open && suggestions.isFetching && places.length === 0 && value.trim().length >= MIN_ADDRESS_CHARS;
  const shownHint = unavailable ? 'Podpowiedzi adresów chwilowo nie działają – wpisz adres ręcznie.' : hint;

  function choose(place: GeoPlaceDto) {
    if (place.precision === 'house') {
      onPick(place.address, { lat: place.lat, lng: place.lng });
      setOpen(false);
    } else {
      // Street or village only: keep typing the number, suggestions follow.
      onType(`${place.line1} `);
      setActive(0);
      document.getElementById(id)?.focus();
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % places.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + places.length) % places.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      choose(places[active]!);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }

  return (
    <Field id={id} label={label} optional={optional} hint={shownHint} error={error}>
      <div className={styles.wrap}>
        <MapPin aria-hidden="true" className={styles.searchIcon} />
        <input
          id={id}
          className={styles.input}
          role="combobox"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          placeholder={placeholder ?? 'np. Słowackiego 15, Wieliczka'}
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList ? `${listId}-${active}` : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, shownHint, error)}
          value={value}
          onChange={(e) => {
            onType(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
        />
        {showList && (
          <ul id={listId} role="listbox" className={styles.list} aria-label="Podpowiedzi adresów">
            {places.map((p, i) => (
              <li
                key={`${p.address}-${i}`}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                className={i === active ? `${styles.option} ${styles.active}` : styles.option}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(p)}
              >
                <span className={styles.name}>{p.line1}</span>
                <span className={styles.meta}>
                  {p.line2}
                  {p.precision !== 'house' && ' – dopisz numer'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {searching && (
        <p className={styles.searching} role="status">
          Szukam podpowiedzi adresu…
        </p>
      )}
      <PinControl address={value} coords={coords} onPin={onPin} />
    </Field>
  );
}
